import React, { useState } from "react";
import { Building2, Users, KeyRound, ArrowRight, Loader2, CheckCircle2, AlertCircle, LogOut } from "lucide-react";
import { db, auth, appId } from "../firebase";
import { signOut } from "firebase/auth";
import AppButton from "./ui/AppButton";
import AppInput from "./ui/AppInput";
import { validateInviteCode } from "../services/inviteCodeService";
import {
    createOrganizationWithInviteCodes,
    joinOrganization,
    migrateDataToOrganization,
    updateUserProfileForOrgCreation,
} from "../services/organizationService";

/**
 * OrganizationSetup - 첫 로그인 시 조직 설정 화면
 * 
 * 사용자가 선택할 수 있는 옵션:
 * 1. 새로운 교회 등록하기 (관리자가 됨)
 * 2. 초대 코드로 참여하기 (관리자/교사 코드에 따라 역할 부여)
 */
export default function OrganizationSetup({ user, userProfile, onComplete }) {
    // 기존 사용자 마이그레이션 여부 확인
    const isExistingUser = userProfile?.churchName && !userProfile?.setupComplete;

    const [mode, setMode] = useState(null); // 'create' | 'join'
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState(false);

    // 새 교회 등록 폼 - 기존 사용자는 정보 미리 채움
    const [churchName, setChurchName] = useState(userProfile?.churchName || "");
    const [deptName, setDeptName] = useState(userProfile?.deptName || "");
    const [userName, setUserName] = useState(userProfile?.name || "");
    const [userRole, setUserRole] = useState(userProfile?.role || "전도사");

    // 초대 코드 참여 폼
    const [inviteCode, setInviteCode] = useState("");
    const [validatedCodeData, setValidatedCodeData] = useState(null); // { orgName, deptName, role }
    const [joinStep, setJoinStep] = useState(1); // 1: enter code, 2: enter profile

    // 새 교회 등록 (관리자로 시작)
    const handleCreateOrganization = async (e) => {
        e.preventDefault();
        if (!churchName.trim() || !deptName.trim() || !userName.trim()) {
            setError("모든 필드를 입력해주세요.");
            return;
        }

        setLoading(true);
        setError("");

        try {
            const { orgId, adminCode, teacherCode } = await createOrganizationWithInviteCodes({
                db,
                userUid: user.uid,
                churchName,
                deptName,
            });

            // [NEW] 기존 사용자라면 데이터 마이그레이션 실행
            if (isExistingUser) {
                console.log("🔄 Existing user detected. Starting data migration...");
                await migrateDataToOrganization({
                    db,
                    appId,
                    uid: user.uid,
                    orgId,
                });
            }

            await updateUserProfileForOrgCreation({
                db,
                userUid: user.uid,
                userName,
                userRole,
                churchName,
                deptName,
                orgId,
                adminCode,
                teacherCode,
            });

            setSuccess(true);
            setTimeout(() => {
                onComplete();
            }, 1500);

        } catch (err) {
            console.error("Organization creation failed:", err);
            setError("조직 생성에 실패했습니다: " + err.message);
        } finally {
            setLoading(false);
        }
    };

    // 초대 코드로 참여 - 1단계: 코드 검증
    const handleValidateInviteCode = async (e) => {
        e.preventDefault();
        if (!inviteCode.trim()) {
            setError("초대 코드를 입력해주세요.");
            return;
        }

        setLoading(true);
        setError("");

        try {
            const validationData = await validateInviteCode(db, inviteCode);
            setValidatedCodeData(validationData);
            setJoinStep(2); // 프로필 입력 단계로 이동

        } catch (err) {
            setError(err.message || "코드 확인 중 오류가 발생했습니다.");
        } finally {
            setLoading(false);
        }
    };

    // 초대 코드로 참여 - 2단계: 가입 완료
    const handleJoinWithProfile = async (e) => {
        e.preventDefault();
        if (!userName.trim()) {
            setError("이름을 입력해주세요.");
            return;
        }

        setLoading(true);
        setError("");

        try {
            await joinOrganization({
                db,
                userUid: user.uid,
                userName,
                userRole,
                validatedCodeData,
            });

            setSuccess(true);
            setTimeout(() => onComplete(), 1500);

        } catch (err) {
            console.error("Join failed:", err);
            setError("가입 처리 중 오류가 발생했습니다.");
        } finally {
            setLoading(false);
        }
    };

    // 성공 화면
    if (success) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-[#F9F9F8] to-[#E8EAE5] flex items-center justify-center p-6">
                <div className="bg-white rounded-3xl p-10 shadow-2xl text-center max-w-md w-full">
                    <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
                        <CheckCircle2 className="text-emerald-500" size={48} />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-800 mb-2">모든 준비가 끝났어요! 🎉</h2>
                    <p className="text-gray-500">이제 동역자들과 함께 사역의 기쁨을 나눠보세요.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-[#F9F9F8] to-[#E8EAE5] flex items-center justify-center p-6">
            <div className="bg-white rounded-3xl p-8 shadow-2xl max-w-md w-full">

                {/* 헤더 */}
                <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-[#354F38]/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                        <Building2 className="text-[#354F38]" size={32} />
                    </div>
                    {isExistingUser ? (
                        <>
                            <h1 className="text-2xl font-black text-gray-800 mb-2">협업 기능 업데이트! 🎉</h1>
                            <p className="text-gray-500 text-sm">
                                이제 부서 선생님들과 함께 사용할 수 있어요!<br />
                                기존 데이터를 유지하면서 조직을 설정해주세요.
                            </p>
                        </>
                    ) : (
                        <>
                            <h1 className="text-2xl font-black text-gray-800 mb-2">반가워요! 우리 시작해 볼까요? 👋</h1>
                            <p className="text-gray-500 text-sm">
                                TheSTAFF를 시작하기 전에<br />
                                사역하실 공동체를 설정해 주세요.
                            </p>
                        </>
                    )}
                </div>

                {/* 모드 선택 */}
                {!mode && (
                    <div className="space-y-4">
                        <AppButton
                            onClick={() => setMode('create')}
                            variant="primary"
                            className="w-full p-5 rounded-2xl flex items-center gap-4"
                        >
                            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                                <Building2 size={24} />
                            </div>
                            <div className="text-left flex-1">
                                <div className="font-bold">우리 공동체 등록하기 (관리자)</div>
                                <div className="text-sm opacity-80">공동체를 만들고 동역자를 초대해요</div>
                            </div>
                            <ArrowRight size={20} />
                        </AppButton>

                        <AppButton
                            onClick={() => setMode('join')}
                            variant="unstyled"
                            className="w-full p-5 bg-white border-2 border-gray-200 text-gray-700 rounded-2xl flex items-center gap-4 hover:border-[#354F38] hover:bg-[#354F38]/5 transition-all"
                        >
                            <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center">
                                <KeyRound size={24} className="text-gray-500" />
                            </div>
                            <div className="text-left flex-1">
                                <div className="font-bold">동역자로 합류하기 (초대 코드)</div>
                                <div className="text-sm text-gray-400">초대 코드를 입력하고 함께 사역해요</div>
                            </div>
                            <ArrowRight size={20} />
                        </AppButton>

                        {/* 로그아웃 버튼 */}
                        <AppButton
                            type="button"
                            onClick={async () => {
                                try {
                                    await signOut(auth);
                                } catch (e) {
                                    console.error("Logout failed:", e);
                                }
                            }}
                            variant="unstyled"
                            className="w-full p-3 text-gray-400 hover:text-gray-600 text-sm font-medium flex items-center justify-center gap-2 transition-colors"
                        >
                            <LogOut size={16} />
                            다른 계정으로 로그인
                        </AppButton>
                    </div>
                )}

                {/* 새 교회 등록 폼 */}
                {mode === 'create' && (
                    <form onSubmit={handleCreateOrganization} className="space-y-4">
                        <button
                            type="button"
                            onClick={() => setMode(null)}
                            className="text-sm text-gray-400 hover:text-gray-600 mb-2"
                        >
                            ← 돌아가기
                        </button>

                        <div className="space-y-1">
                            <label className="text-xs font-bold text-gray-500 ml-1">교회명 *</label>
                            <AppInput
                                type="text"
                                value={churchName}
                                onChange={(e) => setChurchName(e.target.value)}
                                placeholder="예: 사랑의교회"
                                className="w-full p-4 rounded-xl"
                            />
                        </div>

                        <div className="space-y-1">
                            <label className="text-xs font-bold text-gray-500 ml-1">담당 부서명 *</label>
                            <AppInput
                                type="text"
                                value={deptName}
                                onChange={(e) => setDeptName(e.target.value)}
                                placeholder="예: 청년부"
                                className="w-full p-4 rounded-xl"
                            />
                        </div>

                        <div className="flex gap-3">
                            <div className="flex-1 space-y-1">
                                <label className="text-xs font-bold text-gray-500 ml-1">성함 *</label>
                                <AppInput
                                    type="text"
                                    value={userName}
                                    onChange={(e) => setUserName(e.target.value)}
                                    placeholder="예: 홍길동"
                                    className="w-full p-4 rounded-xl"
                                />
                            </div>
                            <div className="w-28 space-y-1">
                                <label className="text-xs font-bold text-gray-500 ml-1">직분 *</label>
                                <AppInput
                                    type="text"
                                    value={userRole}
                                    onChange={(e) => setUserRole(e.target.value)}
                                    placeholder="전도사"
                                    className="w-full p-4 rounded-xl"
                                />
                            </div>
                        </div>

                        {error && (
                            <div className="flex items-center gap-2 text-rose-500 text-sm bg-rose-50 p-3 rounded-xl">
                                <AlertCircle size={16} />
                                {error}
                            </div>
                        )}

                        <AppButton
                            type="submit"
                            disabled={loading}
                            variant="primary"
                            className="w-full p-4 rounded-2xl disabled:opacity-50 flex items-center justify-center gap-2"
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="animate-spin" size={20} />
                                    처리 중...
                                </>
                            ) : (
                                "공동체 만들기"
                            )}
                        </AppButton>
                    </form>
                )}

                {/* 초대 코드 참여 폼 */}
                {mode === 'join' && (
                    <div className="space-y-4">
                        <button
                            type="button"
                            onClick={() => {
                                if (joinStep === 2) {
                                    setJoinStep(1);
                                    setValidatedCodeData(null);
                                } else {
                                    setMode(null);
                                }
                                setError("");
                            }}
                            className="text-sm text-gray-400 hover:text-gray-600 mb-2"
                        >
                            ← 돌아가기
                        </button>

                        {joinStep === 1 ? (
                            <form onSubmit={handleValidateInviteCode} className="space-y-4">
                                <div className="space-y-1">
                                    <label className="text-xs font-bold text-gray-500 ml-1">초대 코드 *</label>
                                    <AppInput
                                        type="text"
                                        value={inviteCode}
                                        onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                                        placeholder="XXXX-ADMIN-XXXX"
                                        className="w-full p-4 rounded-xl font-mono text-center text-lg tracking-wider"
                                        maxLength={15}
                                    />
                                </div>

                                <div className="bg-gray-50 p-4 rounded-xl text-xs text-gray-500 space-y-1">
                                    <p>💡 관리자로부터 받은 초대 코드를 입력해 주세요. 공동체의 일원이 될 수 있도록 안내해 드릴게요. ✨</p>
                                </div>

                                {error && (
                                    <div className="flex items-center gap-2 text-rose-500 text-sm bg-rose-50 p-3 rounded-xl">
                                        <AlertCircle size={16} />
                                        {error}
                                    </div>
                                )}

                                <AppButton
                                    type="submit"
                                    disabled={loading || inviteCode.length < 10}
                                    variant="primary"
                                    className="w-full p-4 rounded-2xl disabled:opacity-50 flex items-center justify-center gap-2"
                                >
                                    {loading ? <Loader2 className="animate-spin" size={20} /> : <CheckCircle2 size={20} />}
                                    코드 확인
                                </AppButton>
                            </form>
                        ) : (
                            <form onSubmit={handleJoinWithProfile} className="space-y-4 animate-fade-in-up">
                                {/* 조직 정보 카드 */}
                                <div className="bg-[#354F38]/5 p-4 rounded-2xl border border-[#354F38]/10 space-y-1">
                                    <div className="text-[10px] font-bold text-[#354F38]/60 uppercase tracking-wider">함께할 소중한 공간</div>
                                    <div className="flex items-center justify-between">
                                        <span className="font-bold text-gray-800">{validatedCodeData.orgName}</span>
                                        <span className="text-xs font-medium bg-white px-2 py-1 rounded-lg border border-gray-100">{validatedCodeData.deptName}</span>
                                    </div>
                                    <div className="text-[10px] text-gray-400 mt-1">
                                        맡겨진 귀한 직무: <span className="text-[#354F38] font-bold">{validatedCodeData.role === 'admin' ? '관리자' : '일반 교사'}</span>
                                    </div>
                                </div>

                                <div className="space-y-3">
                                    <div className="space-y-1">
                                        <label className="text-xs font-bold text-gray-500 ml-1">내 이름 *</label>
                                        <AppInput
                                            type="text"
                                            value={userName}
                                            onChange={(e) => setUserName(e.target.value)}
                                            placeholder="예: 홍길동"
                                            className="w-full p-4 rounded-xl"
                                            required
                                        />
                                    </div>
                                    <div className="space-y-1">
                                        <label className="text-xs font-bold text-gray-500 ml-1">직분</label>
                                        <AppInput
                                            type="text"
                                            value={userRole}
                                            onChange={(e) => setUserRole(e.target.value)}
                                            placeholder="예: 교사, 청년"
                                            className="w-full p-4 rounded-xl"
                                        />
                                    </div>
                                </div>

                                {error && (
                                    <div className="flex items-center gap-2 text-rose-500 text-sm bg-rose-50 p-3 rounded-xl">
                                        <AlertCircle size={16} />
                                        {error}
                                    </div>
                                )}

                                <AppButton
                                    type="submit"
                                    disabled={loading}
                                    variant="primary"
                                    className="w-full p-4 rounded-2xl disabled:opacity-50 flex items-center justify-center gap-2"
                                >
                                    {loading ? <Loader2 className="animate-spin" size={20} /> : <Users size={20} />}
                                    기쁘게 합류하기
                                </AppButton>
                            </form>
                        )}
                    </div>
                )}

            </div>
        </div>
    );
}
